import {
  capacidadeDaMaquina,
  CatalogoOpencodeSchema,
  CHAVES_DE_CAPACIDADE,
  CODIGOS_ERRO,
  ESFORCO_PADRAO_OPENCODE,
  ESFORCO_SESSAO,
  EsforcoSchema,
  FRASES_DOS_ERROS_DO_OPENCODE,
  MODOS_PERMISSAO,
  modoDaMestreEhFixo,
  modoInicialDaMestre,
  motorDisponivelNaMaquina,
  nomeDoProvedorOpencode,
  versaoAtende,
  versaoDoDaemonQueFala,
  type ChaveDeCapacidade,
  type ModeloCombinado,
  type ModeloDaMaquina,
  type ModeloOpencode,
  type VeredictoDeCapacidade,
} from '@remoto/shared';
import {
  ESFORCO_SEM_VARIANTE,
  motorNaoTratado,
  provedorOpencodeNaoTratado,
  utilizacaoDoMotor,
  type CotaClaude,
  type CotaMotor,
  type Motor,
  type ProvedorOpencode,
} from '@remoto/protocolo';
import { ErroApi } from '../api';
import {
  opcoesDeEsforco as opcoesDeEsforcoDoModelo,
  ROTULO_DO_ESFORCO_PADRAO,
  type OpcaoDeEsforco,
} from '../configuracoes/regras';

/**
 * O que muda, por MOTOR, nas duas telas de criar (sessão e mestre).
 *
 * Mora fora dos componentes por dois motivos. O primeiro é a guarda dos ramos
 * binários (`packages/shared/tests/ramos-de-motor.spec.ts`): com três motores,
 * `motor === 'CODEX' ? a : b` manda o OpenCode para o `b` calado, e a regra da
 * casa é `switch` que termina em `motorNaoTratado`. O segundo é que cada
 * decisão aqui é regra — de onde sai o catálogo, que esforço vale, por que um
 * motor está fechado —, e regra escondida em componente não tem teste.
 */

/** Do mais barato ao mais caro; é a ordem em que a pessoa pensa no assunto. */
const ESFORCOS_DA_CASA = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

/* -------------------------------------------------------------------------- */
/* O catálogo do provedor de modelos, como a tela o recebe                     */
/* -------------------------------------------------------------------------- */

/**
 * O que a tela sabe do catálogo do OpenCode (`GET /opencode/modelos`).
 *
 * `recurso_desligado` é resposta, não falha: com `RECURSO_OPENCODE` desligado
 * (D32, o estado de nascença em produção) a API devolve 404, e é por ele que a
 * tela descobre que o motor não existe neste painel — antes de a pessoa
 * escolher e esbarrar na criação.
 */
export type CatalogoOpencodeNaTela =
  | { estado: 'carregando' }
  | { estado: 'pronto'; modelos: ModeloOpencode[] }
  | { estado: 'recurso_desligado' }
  | { estado: 'falhou' };

export function lerCatalogoOpencode(dados: unknown, erro: unknown): CatalogoOpencodeNaTela {
  if (erro instanceof ErroApi) {
    const desligado =
      erro.codigo === CODIGOS_ERRO.recursoOpencodeDesligado || erro.status === 404;
    return desligado ? { estado: 'recurso_desligado' } : { estado: 'falhou' };
  }
  if (erro) return { estado: 'falhou' };
  if (dados === undefined) return { estado: 'carregando' };
  /*
    Pela forma, e nunca por confiança: uma resposta que não é o catálogo vira
    `falhou`, e a tela diz que não conseguiu ler — em vez de desenhar um
    seletor vazio que parece "o provedor não tem modelo nenhum".
  */
  const lido = CatalogoOpencodeSchema.safeParse(dados);
  return lido.success ? { estado: 'pronto', modelos: lido.data.modelos } : { estado: 'falhou' };
}

/**
 * Se o recurso OpenCode está ligado NESTE painel (D32), pelos dois sinais que
 * a API dá: `recursos.opencode` do `/auth/eu` e o 404 das rotas dele.
 *
 * `=== false`, e não `!== true`: uma API que ainda não manda o campo não
 * fecha o motor por omissão — quem responde, aí, é o 404 do catálogo, que é o
 * comportamento de verdade das rotas com o recurso desligado.
 */
export function recursoOpencodeLigado(
  recursos: { opencode?: boolean } | undefined,
  catalogo: CatalogoOpencodeNaTela,
): boolean {
  return recursos?.opencode !== false && catalogo.estado !== 'recurso_desligado';
}

/** Favoritos primeiro (D12), sem mexer na ordem do resto — a API já ordenou. */
export function favoritosPrimeiro(modelos: readonly ModeloOpencode[]): ModeloOpencode[] {
  return [...modelos.filter((m) => m.favorito), ...modelos.filter((m) => !m.favorito)];
}

/**
 * O catálogo do OpenCode na forma que `combinarModelos` consome, com o padrão
 * DA FROTA marcado — o que `modeloPadraoOpencode` procura antes de cair na
 * constante da casa.
 */
function paraModeloDaMaquina(modelo: ModeloOpencode, padraoDaFrota: string | null): ModeloDaMaquina {
  return {
    id: modelo.id,
    rotulo: modelo.nome,
    esforcosDoMotor: modelo.esforcos,
    esforcoPadrao: ESFORCO_PADRAO_OPENCODE,
    ...(modelo.id === padraoDaFrota ? { padrao: true } : {}),
  };
}

/**
 * De onde sai a lista de modelos de cada motor, na tela de criar.
 *
 * Claude: da casa (`MODELOS`), então a máquina não precisa dizer nada. Codex:
 * do `model/list` que a MÁQUINA reportou — sem ele não há modelo, porque não
 * existe padrão da casa. OpenCode: do catálogo do PROVEDOR ATIVO da frota (F2:
 * o NanoGPT; o OpenRouter fica guardado), pela API, o mesmo para a frota
 * inteira, e grande o bastante para pedir busca (D12).
 *
 * `do-provedor`, e não o nome de um deles: a origem é a mesma rota com
 * qualquer provedor, e um nome aqui envelheceria na primeira troca — como
 * `do-openrouter` envelheceu na F2.
 */
export type OrigemDoCatalogo = 'da-casa' | 'da-maquina' | 'do-provedor';

export function origemDoCatalogo(motor: Motor): OrigemDoCatalogo {
  switch (motor) {
    case 'CLAUDE':
      return 'da-casa';
    case 'CODEX':
      return 'da-maquina';
    case 'OPENCODE':
      return 'do-provedor';
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * A lista de modelos de cada motor, na forma que `combinarModelos` consome.
 *
 * `padraoOpencode` é o `padrao.modelo` do catálogo — o modelo padrão que o
 * dono escolheu na tela de Configurações. **Sem ele a tela de criar marcava a
 * constante da casa**, que desde a F2 é um modelo do NanoGPT: com o OpenRouter
 * ativo, ela viria com um modelo que a API recusa (`modelo_de_outro_provedor`)
 * depois de tudo preenchido. Ausente (API anterior, catálogo não chegou), vale
 * a constante, como antes.
 */
export function catalogoDoMotor(
  motor: Motor,
  fontes: {
    codex: readonly ModeloDaMaquina[];
    opencode: readonly ModeloOpencode[];
    padraoOpencode?: string | null;
  },
): ModeloDaMaquina[] {
  switch (motor) {
    case 'CLAUDE':
      return [];
    case 'CODEX':
      return [...fontes.codex];
    case 'OPENCODE':
      return favoritosPrimeiro(fontes.opencode).map((m) => paraModeloDaMaquina(m, fontes.padraoOpencode ?? null));
    default:
      return motorNaoTratado(motor);
  }
}

/* -------------------------------------------------------------------------- */
/* Por que um motor está fechado nesta máquina                                  */
/* -------------------------------------------------------------------------- */

/** A forma mínima da máquina que decide o OpenCode: papel, sonda e o retrato do pulso. */
export interface MaquinaParaOpencode {
  papel?: string;
  capacidades?: unknown;
  /** O retrato `maquinas.opencode` (pulso). Lido com cuidado: é jsonb vindo de fora. */
  opencode?: { chave?: { estado?: string } | null } | null;
  /** A versão do daemon (`GET /maquinas`): é ela que diz se ele fala o provedor ativo (F2). */
  versaoAgente?: string | null;
}

/**
 * A frase de "falta a chave", com o NOME do provedor ativo quando se sabe
 * (F2): "falta a chave do NanoGPT" manda o ADMIN ao campo certo da tela de
 * Configurações, que agora tem dois. Sem provedor conhecido, a do shared.
 */
export function fraseDaFaltaDeChave(provedor: ProvedorOpencode | null): string {
  if (provedor === null) return FRASES_DOS_ERROS_DO_OPENCODE[CODIGOS_ERRO.opencodeSemChave];
  return `Falta a chave do ${nomeDoProvedorOpencode(provedor)}. Um ADMIN cadastra na tela de Configurações.`;
}

/**
 * O daemon desta máquina é anterior ao que fala o provedor ativo? (F2)
 *
 * Só com as duas coisas sabidas: versão ilegível ou ausente não é "antiga" —
 * é "não sei", e aí vale a frase da chave, que a API confirma na criação.
 */
function daemonAntigoParaOProvedor(versao: string | null | undefined, provedor: ProvedorOpencode | null): boolean {
  if (provedor === null || typeof versao !== 'string' || !/^\d+\.\d+\.\d+/.test(versao)) return false;
  return !versaoAtende(versao, versaoDoDaemonQueFala(provedor));
}

/**
 * O motivo de o OpenCode NÃO poder ser escolhido, ou `null` quando pode.
 *
 * A ordem é a do gesto que resolve, do mais largo ao mais estreito: o recurso
 * do painel inteiro (D32), a VPS (D1: lá só mora a mestre), a sonda daquela
 * máquina e, por fim, a chave da frota chegando nela. Cada um pede uma pessoa
 * diferente — o dono do painel, quem escolhe a máquina, o dono da máquina, o
 * ADMIN na tela de Configurações —, e por isso a frase é a do caso, nunca um
 * "indisponível" genérico.
 *
 * Retrato AUSENTE não fecha nada: ele só diz "não sei" (daemon anterior, API
 * anterior). Quem responde pela chave, aí, é a API na criação, com
 * `opencode_sem_chave`. Catálogo lento ou fora do ar também não fecha: o
 * padrão da D13 existe sem ele.
 *
 * **Chave ausente com daemon antigo é "daemon desatualizado"** (F2): o daemon
 * anterior ao 0.8.0 não fala o NanoGPT, a API manda `chave: null` para ele, e
 * o retrato diz `ausente` — mas colar a chave de novo não resolve nada; o que
 * resolve é atualizar o daemon. `provedor` é o ATIVO (do catálogo), e `null`
 * quando não se sabe.
 */
export function motivoOpencodeIndisponivel(
  recursoLigado: boolean,
  maquina: MaquinaParaOpencode | undefined,
  provedor: ProvedorOpencode | null = null,
): string | null {
  if (!recursoLigado) {
    return FRASES_DOS_ERROS_DO_OPENCODE[CODIGOS_ERRO.recursoOpencodeDesligado];
  }
  const naMaquina = motorDisponivelNaMaquina(
    'OPENCODE',
    maquina?.papel === 'VPS' ? 'VPS' : 'PC',
    'NORMAL',
  );
  if (!naMaquina.ok) return FRASES_DOS_ERROS_DO_OPENCODE[naMaquina.codigo];

  const sonda = capacidadeDaMaquina(maquina?.capacidades, CHAVES_DE_CAPACIDADE.opencode);
  if (sonda === null) return 'esta máquina não reportou o OpenCode';
  if (sonda.estado !== 'aprovada') {
    return sonda.motivo || FRASES_DOS_ERROS_DO_OPENCODE[CODIGOS_ERRO.opencodeIndisponivel];
  }

  const chave = maquina?.opencode?.chave?.estado;
  if (chave === 'ausente') {
    if (provedor !== null && daemonAntigoParaOProvedor(maquina?.versaoAgente, provedor)) {
      return (
        `O daemon desta máquina (${maquina?.versaoAgente}) é anterior ao que fala o ` +
        `${nomeDoProvedorOpencode(provedor)} (${versaoDoDaemonQueFala(provedor)}). Atualize o daemon dela.`
      );
    }
    return fraseDaFaltaDeChave(provedor);
  }
  if (chave === 'erro') {
    return 'A chave do provedor de modelos não foi aplicada nesta máquina. Um ADMIN confere o estado na tela de Configurações.';
  }
  return null;
}

/** O que a opção desabilitada mostra: a linha de baixo e o `title`. */
export interface MotorFechado {
  descricao: string;
  motivo: string;
}

/**
 * A opção de motor desabilitada, com o motivo — ou `null` quando está aberta.
 *
 * A opção NÃO some quando falta capacidade: sumir não ensina nada a quem
 * precisa ligar a chave. No OpenCode a linha de baixo JÁ é o motivo, porque
 * no telefone o `title` não aparece e os quatro motivos pedem gestos
 * diferentes.
 */
export function motorFechado(
  motor: Motor,
  contexto: { codex: VeredictoDeCapacidade | null; motivoOpencode: string | null },
): MotorFechado | null {
  switch (motor) {
    case 'CLAUDE':
      return null;
    case 'CODEX':
      return contexto.codex?.estado === 'aprovada'
        ? null
        : {
            descricao: 'indisponível nesta máquina',
            motivo: contexto.codex?.motivo || 'esta máquina não reportou o Codex',
          };
    case 'OPENCODE':
      return contexto.motivoOpencode === null
        ? null
        : { descricao: contexto.motivoOpencode, motivo: contexto.motivoOpencode };
    default:
      return motorNaoTratado(motor);
  }
}

/* -------------------------------------------------------------------------- */
/* Cota, esforço e confirmação                                                 */
/* -------------------------------------------------------------------------- */

/**
 * O texto de cada motor na linha das cotas.
 *
 * "sem leitura recente" e não "0%": cota velha e cota vazia não são zero. E o
 * OpenCode não tem cota POR MÁQUINA para mostrar aqui: no OpenRouter ele paga
 * por token (D14); no NanoGPT (F2) gasta a assinatura semanal da frota e,
 * acabada ela, o crédito — e a assinatura mora no cartão do OpenCode, na tela
 * de Uso. Dizer "sem leitura" dele mandaria esperar uma leitura que nunca vem.
 *
 * `provedorOpencode` `null` é "não sei" (o catálogo não chegou): a frase não
 * nomeia ninguém.
 */
export function textoDaCota(
  motor: Motor,
  cotas: { claude: CotaClaude | null; codex: CotaMotor | null },
  agora: Date,
  provedorOpencode: ProvedorOpencode | null = null,
): string {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX': {
      const fracao = utilizacaoDoMotor(motor, cotas, agora);
      return fracao === null ? 'sem leitura recente' : `${Math.round(fracao * 100)}% usados`;
    }
    case 'OPENCODE':
      return textoDaCotaDoOpencode(provedorOpencode);
    default:
      return motorNaoTratado(motor);
  }
}

function textoDaCotaDoOpencode(provedor: ProvedorOpencode | null): string {
  if (provedor === null) return 'paga ao provedor de modelos, sem cota aqui';
  switch (provedor) {
    case 'nanogpt':
      return 'assinatura do NanoGPT, depois crédito';
    case 'openrouter':
      return 'paga por token, sem cota';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * O esforço que a tela marca ao trocar de motor.
 *
 * Cada motor recusa o do outro: o `padrao` do OpenCode é recusado por Claude e
 * Codex (Interface congelada item 3), e o `xhigh` do Claude pode não existir
 * no modelo do OpenCode. Carregar o valor de um motor para o outro faria a
 * criação voltar com `esforco_desconhecido` depois de tudo preenchido.
 */
export function esforcoAoTrocarDeMotor(
  novo: Motor,
  padraoDoCatalogo: ModeloCombinado | undefined,
  atual: string,
): string {
  switch (novo) {
    case 'CLAUDE':
      return EsforcoSchema.safeParse(atual).success ? atual : ESFORCO_SESSAO;
    case 'CODEX':
      return (
        padraoDoCatalogo?.esforcoPadrao ?? (atual === ESFORCO_SEM_VARIANTE ? ESFORCO_SESSAO : atual)
      );
    case 'OPENCODE':
      return ESFORCO_PADRAO_OPENCODE;
    default:
      return motorNaoTratado(novo);
  }
}

/**
 * Os esforços que o seletor oferece.
 *
 * Claude e Codex: os do MODELO quando ele os reporta, a lista da casa quando
 * não. OpenCode (R7): a regra é a da tela de Configurações
 * (`configuracoes/regras.ts`) — "Padrão do provedor" primeiro e depois só os
 * `variant` DAQUELE modelo; o DeepSeek flash não tem `medium`, e o Kimi não
 * tem nenhum. Uma lista de um item só é a tela dizendo que não há escolha.
 */
export function opcoesDeEsforco(motor: Motor, escolhido: ModeloCombinado | undefined): OpcaoDeEsforco[] {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return (escolhido?.esforcos.length ? escolhido.esforcos : [...ESFORCOS_DA_CASA]).map((e) => ({
        id: e,
        rotulo: e,
      }));
    case 'OPENCODE':
      return opcoesDeEsforcoDoModelo(escolhido);
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * Se a conversa deste motor sai da rede de casa (D10, D11): é o que exige a
 * `confirmacaoOpencode` e o selo.
 */
export function motorSaiDeCasa(motor: Motor): boolean {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return false;
    case 'OPENCODE':
      return true;
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * O texto da caixa de confirmação do OpenCode (D10), igual nas três telas
 * (criar sessão, trocar de motor, criar mestre).
 *
 * Nomeia o provedor ATIVO (F2 da revisão de out/2026): é para ele que a
 * conversa vai, e o que se gasta é dele — a assinatura do NanoGPT primeiro e o
 * crédito depois (D40), ou o crédito da chave do OpenRouter. Começa sempre por
 * "Esta conversa sai da rede de casa (", que é o que a pessoa procura ao ler.
 * Sem provedor conhecido, não nomeia ninguém.
 */
export function textoDaConfirmacaoOpencode(provedor: ProvedorOpencode | null): string {
  if (provedor === null) {
    return 'Esta conversa sai da rede de casa (o provedor de modelos da frota e o provedor do modelo) e gasta a assinatura ou o crédito dele.';
  }
  const nome = nomeDoProvedorOpencode(provedor);
  switch (provedor) {
    case 'nanogpt':
      return `Esta conversa sai da rede de casa (${nome} e o provedor do modelo) e gasta a assinatura do ${nome} da frota — e o crédito, quando a cota da semana acaba.`;
    case 'openrouter':
      return `Esta conversa sai da rede de casa (${nome} e o provedor do modelo) e gasta crédito da chave da frota.`;
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/* -------------------------------------------------------------------------- */
/* A mestre                                                                    */
/* -------------------------------------------------------------------------- */

/** De onde sai o modelo da mestre de cada motor. */
export type OrigemDoModeloDaMestre = 'politica-da-casa' | 'catalogo-da-maquina' | 'configuracoes';

export interface PerfilDaMestre {
  /** A capacidade que a sonda da máquina tem de ter aprovado. */
  capacidade: ChaveDeCapacidade;
  modelo: OrigemDoModeloDaMestre;
  /**
   * A pasta que o campo traz ao escolher o motor. Só o Claude tem uma da casa:
   * a pasta de uma mestre é a MEMÓRIA dela, e duas mestres na mesma pasta
   * leriam e escreveriam o `MEMORIA.md` uma da outra.
   */
  pastaPadrao: string;
}

export function perfilDaMestre(motor: Motor): PerfilDaMestre {
  switch (motor) {
    case 'CLAUDE':
      return {
        capacidade: CHAVES_DE_CAPACIDADE.mestre,
        modelo: 'politica-da-casa',
        pastaPadrao: '/home/claude/mestre',
      };
    case 'CODEX':
      return {
        capacidade: CHAVES_DE_CAPACIDADE.mestreCodex,
        modelo: 'catalogo-da-maquina',
        pastaPadrao: '',
      };
    case 'OPENCODE':
      /*
        O modelo e o esforço da mestre do OpenCode moram na tela de
        Configurações (D2, D17), e a API RECUSA os dois na criação
        (`modelo_da_mestre_vem_da_config`): um segundo lugar para escolhê-los
        seria uma segunda resposta para "qual modelo a mestre usa".
      */
      return {
        capacidade: CHAVES_DE_CAPACIDADE.mestreOpencode,
        modelo: 'configuracoes',
        pastaPadrao: '',
      };
    default:
      return motorNaoTratado(motor);
  }
}

function rotuloDoModo(modo: string): string {
  return MODOS_PERMISSAO.find((m) => m.id === modo)?.rotulo ?? modo;
}

/**
 * O que a pessoa confirma ao criar a mestre: o modo em que ela NASCE.
 *
 * Lido de `modoInicialDaMestre` e `modoDaMestreEhFixo`, e não escrito à mão
 * por motor: se um dia a mestre do OpenCode deixar de ser fixa, esta frase
 * muda junto — em vez de continuar prometendo o que o servidor não faz mais.
 */
export function avisoDoModoDaMestre(motor: Motor): string {
  const modo = modoInicialDaMestre(motor);
  if (modoDaMestreEhFixo(motor)) {
    return (
      `Entendo que esta mestre nasce em «${rotuloDoModo(modo)}», e FIXO: nada do que ela faz ` +
      'pede permissão, e o modo não troca depois. A trava é a lista de negação da máquina, e só ' +
      'as consultas do cérebro que gastam crédito ou escrevem pedem cartão — tudo fica visível na ' +
      'conversa e na auditoria.'
    );
  }
  return (
    `Entendo que o mestre nasce em modo ${rotuloDoModo(modo).toLowerCase()}: as consultas e os ` +
    'despachos dele não pedem permissão — tudo fica visível na conversa e na auditoria.'
  );
}

/**
 * A mestre pode NASCER em bypass (ADR-0023) nesta máquina? Só onde a API diz
 * que o bypass total vale (`bypassTotal`, as duas chaves) — e nunca a do
 * OpenCode, que já nasce em bypass FIXO e não tem o que escolher.
 */
export function mestrePodeNascerEmBypass(motor: Motor, bypassTotal: boolean | undefined): boolean {
  return bypassTotal === true && !modoDaMestreEhFixo(motor);
}

/**
 * O que a pessoa confirma ao pedir a mestre já em bypass. A frase muda com o
 * lugar, porque o efeito muda: no Codex da VPS o bypass é `never` DENTRO do
 * sandbox (M0-25), e dizer "nada limita" ali seria mentir para o lado
 * assustador; no resto, `bypassPermissions` não checa nada.
 */
export function avisoDoBypassDaMestre(motor: Motor, papelDaMaquina: string): string {
  const semLimite =
    'Entendo que esta mestre nasce em «Ignorar permissões»: nada pede permissão e nada é negado ' +
    'pelo motor — tudo fica visível na conversa e na auditoria.';
  switch (motor) {
    case 'CODEX':
      return papelDaMaquina === 'VPS'
        ? 'Entendo que esta mestre nasce em «Ignorar permissões»: nada pede permissão nem passa pelo ' +
            'revisor, e o sandbox continua — sem rede, escrevendo só na pasta dela.'
        : semLimite;
    // A do OpenCode nem chega aqui (`mestrePodeNascerEmBypass`); se chegar, nada a separa do Claude.
    case 'CLAUDE':
    case 'OPENCODE':
      return semLimite;
    default:
      return motorNaoTratado(motor);
  }
}

/** O esforço como a tela o escreve: o `padrao` tem nome de gente. */
export function rotuloDoEsforco(esforco: string): string {
  return esforco === ESFORCO_SEM_VARIANTE ? ROTULO_DO_ESFORCO_PADRAO.toLowerCase() : esforco;
}
